import { isDevPreview } from '../boot/devPreview';
import { sb } from './supabase';
import { logActivity } from './repo';
import { formatOf, type CadFormat, type ConversionWarning } from '../cad/format/adapter';
import { newEntityId, type UnderlayRef } from '../cad/model/document';
import { UM_PER_MM } from '../cad/model/units';
import type { Pt } from '../cad/model/geom';

/**
 * 업로드 파이프라인 (작업지시서 P3-04 / P3-05 / P3-15)
 *
 * 원칙 (기획서 4.3절 / v2 §3.2):
 *   1. **원본은 절대 건드리지 않는다.** 업로드한 바이트 그대로 보관하고,
 *      변환 결과는 별도 버킷에 둔다. DB 트리거로도 이중 차단돼 있다.
 *   2. 변환은 워커가 한다. 브라우저는 작업을 **큐에 넣고 결과를 구독**할 뿐이다.
 *      워커는 인바운드 포트를 열지 않고 이 테이블을 폴링한다.
 *   3. 다운로드는 만료되는 Signed URL 로만. 모든 반출은 감사 로그에 남는다.
 */

/** 업로드 허용 한도 */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024; // 500MB

const ALLOWED: CadFormat[] = ['dwg', 'dxf', 'dwt', 'svg', 'pdf', 'png', 'jpg'];

export interface UploadValidation {
  ok: boolean;
  format?: CadFormat;
  reason?: string;
}

/**
 * 업로드 전 검사.
 * 확장자만 믿지 않는다 — 서버(RLS·Storage 정책)에서도 다시 막지만,
 * 사용자에게 즉시 알려주는 편이 훨씬 낫다.
 */
export function validateUpload(file: { name: string; size: number }): UploadValidation {
  const format = formatOf(file.name);
  if (!format) {
    return { ok: false, reason: '지원하지 않는 파일 형식입니다. (DWG/DXF/DWT/SVG/PDF/PNG/JPG)' };
  }
  if (!ALLOWED.includes(format)) {
    return { ok: false, reason: `${format.toUpperCase()} 형식은 업로드할 수 없습니다.` };
  }
  if (file.size <= 0) {
    return { ok: false, reason: '빈 파일입니다.' };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    const mb = Math.round(file.size / 1024 / 1024);
    return { ok: false, reason: `파일이 너무 큽니다 (${mb}MB). 최대 500MB 까지 가능합니다.` };
  }
  return { ok: true, format };
}

/* -------------------------------------------------------------------------- *
 * DWG 버전 판별
 * -------------------------------------------------------------------------- */

/**
 * DWG 파일 맨 앞 6바이트가 곧 버전 코드다. (예: "AC1027")
 * 압축을 풀 필요도, 서버에 올릴 필요도 없다.
 */
const DWG_VERSION_NAME: Record<string, string> = {
  AC1009: 'R11/R12',
  AC1012: 'R13',
  AC1014: 'R14',
  AC1015: '2000',
  AC1018: '2004',
  AC1021: '2007',
  AC1024: '2010',
  AC1027: '2013',
  AC1032: '2018',
};

export interface DwgVersionInfo {
  /** 원시 코드 — 예: 'AC1027' */
  code: string;
  /** 사람이 아는 이름 — 예: '2013' */
  label: string;
  /**
   * 변환기가 제대로 못 읽을 가능성이 큰가.
   *
   * ★ LibreDWG 는 R2000 이하는 잘 읽지만 그 이후 형식에서는 모델 공간을
   *   통째로 잃어버리는 일이 있다. 실제로 AC1027 도면에서 집기 블록 정의는
   *   남고 벽체·배치(INSERT)가 전부 사라져 화면이 빈 적이 있다.
   *   변환에 몇 분을 기다린 뒤에 알게 되면 사용자는 "먹통" 으로 받아들인다.
   *   → 올리기 전에 미리 알려 준다.
   */
  risky: boolean;
}

/** DWG/DWT 면 버전을 읽는다. DWG 가 아니거나 못 읽으면 null. */
export async function readDwgVersion(file: Blob): Promise<DwgVersionInfo | null> {
  try {
    const head = new Uint8Array(await file.slice(0, 6).arrayBuffer());
    if (head.length < 6) return null;
    const code = String.fromCharCode(...head);
    if (!/^AC\d{4}$/.test(code)) return null;
    return {
      code,
      label: DWG_VERSION_NAME[code] ?? code,
      // R2000(AC1015) 이하만 안전하다고 본다
      risky: !['AC1009', 'AC1012', 'AC1014', 'AC1015'].includes(code),
    };
  } catch {
    return null;
  }
}

/** SHA-256 — 중복 업로드 감지와 무결성 확인에 쓴다 */
export async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Storage 경로.
 * 첫 세그먼트가 project_id 여야 한다 — Storage 정책이 그것으로 멤버십을 판정한다
 * (0006_cad_storage.sql).
 */
export function buildStoragePath(opts: {
  projectId: string;
  drawingId: string;
  versionId?: string | null;
  category: 'source' | 'derived' | 'preview' | 'export';
  filename: string;
}): string {
  const safe = sanitizeFilename(opts.filename);
  const version = opts.versionId ?? '-';
  return `${opts.projectId}/${opts.drawingId}/${version}/${opts.category}/${crypto.randomUUID()}-${safe}`;
}

/**
 * 파일명 정리 — Storage **키**에 넣을 이름을 만든다. 사람이 보는 이름이 아니다.
 *
 * ★ 예전에는 "한글은 그대로 둔다" 는 원칙으로 일부 문자만 블록리스트로 걸렀는데,
 *   실제로 `(리뉴얼)` 처럼 괄호가 든 파일명에서 Supabase Storage 가
 *   `Invalid key` 로 업로드를 거부했다. Storage 가 정확히 어떤 문자까지
 *   허용하는지 문서화되어 있지 않아 블록리스트로는 계속 새로운 문자에서
 *   터질 수 있다 — 그래서 **허용리스트**로 뒤집는다: 영문·숫자·점·하이픈·
 *   밑줄만 통과시키고 나머지(한글·공백·괄호 등)는 전부 `_` 로 바꾼다.
 *
 *   사람이 보는 파일명은 이 함수를 거치지 않는다. `cad_source.original_filename`
 *   에 원본 그대로 저장되고, 화면은 항상 그 컬럼을 보여준다 — 여기서
 *   한글이 사라져도 사용자에게는 아무 영향이 없다.
 */
export function sanitizeFilename(name: string): string {
  // 경로 구분자를 잘라 마지막 조각만 쓴다 -> `../` 이스케이프 차단
  const base = name.split(/[\\/]/).pop() ?? 'file';

  const cleaned = base
    // 허용: 영문·숫자·점·하이픈·밑줄. 그 외(한글·공백·괄호·특수문자 전부)는 밑줄로.
    .replace(/[^A-Za-z0-9._-]/g, '_')
    // 밑줄이 길게 이어지는 걸 줄인다 — 안 그러면 한글 구간이 밑줄 도배로 남는다
    .replace(/_{2,}/g, '_')
    // 숨김파일 위장 방지 — 앞쪽 "점"만 제거한다. 밑줄까지 같이 지우면
    // "도면.dxf" 같은(한글만 있던) 이름이 "_.dxf" → "dxf" 로 확장자 앞
    // 구분자까지 날아간다. 실제로 겪은 사고라 점만 정확히 좁혀 걷어낸다.
    .replace(/^\.+/, '')
    .slice(0, 180);

  // 영문·숫자가 하나도 안 남았으면(전부 밑줄/점/하이픈뿐) 의미 없는 이름이다.
  return /[A-Za-z0-9]/.test(cleaned) ? cleaned : 'file';
}

/* -------------------------------------------------------------------------- *
 * 업로드
 * -------------------------------------------------------------------------- */

export interface UploadedSource {
  id: string;
  storagePath: string;
  sha256: string;
  format: CadFormat;
  size: number;
  /** 같은 해시의 원본이 이미 있었다면 그 id */
  duplicateOf?: string;
}

export async function uploadSource(params: {
  projectId: string;
  drawingId: string;
  versionId?: string | null;
  file: File;
}): Promise<UploadedSource> {
  const { projectId, drawingId, versionId, file } = params;

  const check = validateUpload(file);
  if (!check.ok) throw new Error(check.reason);
  const format = check.format!;

  if (isDevPreview()) {
    throw new Error('개발 미리보기 모드에서는 업로드할 수 없습니다. 실제 로그인 후 이용하세요.');
  }

  const buf = await file.arrayBuffer();
  const sha256 = await sha256Hex(buf);
  const client = sb();

  // 같은 내용이 이미 올라와 있으면 다시 올리지 않는다.
  // 같은 도면을 반복 업로드하는 실무 패턴에서 저장공간을 크게 아낀다.
  const { data: dup } = await client
    .from('cad_source')
    .select('id, storage_path')
    .eq('drawing_id', drawingId)
    .eq('sha256', sha256)
    .maybeSingle();

  if (dup) {
    const d = dup as { id: string; storage_path: string };
    return {
      id: d.id,
      storagePath: d.storage_path,
      sha256,
      format,
      size: file.size,
      duplicateOf: d.id,
    };
  }

  const storagePath = buildStoragePath({
    projectId,
    drawingId,
    versionId,
    category: 'source',
    filename: file.name,
  });

  const { error: upErr } = await client.storage.from('cad-originals').upload(storagePath, file, {
    contentType: file.type || 'application/octet-stream',
    // 원본은 덮어쓰지 않는다. 경로에 uuid 가 있어 충돌할 일도 없다.
    upsert: false,
  });
  if (upErr) throw new Error(`원본 업로드 실패: ${upErr.message}`);

  const { data, error } = await client
    .from('cad_source')
    .insert({
      drawing_id: drawingId,
      version_id: versionId ?? null,
      original_filename: file.name,
      file_format: format,
      mime_type: file.type || null,
      storage_path: storagePath,
      file_size: file.size,
      sha256,
      conversion_status: 'queued',
    })
    .select('id')
    .single();

  if (error) {
    // DB 등록이 실패하면 올린 파일을 남겨두지 않는다 (고아 파일 방지)
    await client.storage.from('cad-originals').remove([storagePath]);
    throw new Error(`원본 등록 실패: ${error.message}`);
  }

  const id = (data as { id: string }).id;
  await logActivity('source.upload', {
    project: projectId,
    drawing: drawingId,
    payload: { filename: file.name, format, size: file.size, sha256 },
  });

  return { id, storagePath, sha256, format, size: file.size };
}

/* -------------------------------------------------------------------------- *
 * 부착(Attach) 이미지 — 신규 요청
 * -------------------------------------------------------------------------- */

/**
 * 래스터화한 PDF 페이지(PNG)를 cad-derived 버킷에 올린다.
 * devPreview 에서는 Storage 가 없으므로 호출하지 않는다 — 그 경우
 * 호출자가 대신 data: URL 을 UnderlayRef.src 에 그대로 넣는다.
 */
export async function uploadUnderlayImage(params: {
  projectId: string;
  drawingId: string;
  versionId?: string | null;
  blob: Blob;
}): Promise<string> {
  const path = buildStoragePath({
    projectId: params.projectId,
    drawingId: params.drawingId,
    versionId: params.versionId,
    category: 'derived',
    filename: 'underlay.png',
  });
  const { error } = await sb()
    .storage.from('cad-derived')
    .upload(path, params.blob, { contentType: 'image/png', upsert: false });
  if (error) throw new Error(`부착 이미지 업로드 실패: ${error.message}`);
  return path;
}

/**
 * 부착 이미지용 만료 URL.
 *
 * `createDownloadUrl()` 과 일부러 분리한다 — 그건 "사용자가 원본을 내려받았다"
 * 는 감사 로그를 남기는데, 도면을 열 때마다 배경 이미지를 그리려고 부르는
 * 이건 다운로드가 아니다. 그래서 감사 로그를 남기지 않고, 캔버스를 오래
 * 켜 둬도 끊기지 않도록 만료 시간을 길게(1시간) 잡는다.
 */
export async function getUnderlaySignedUrl(path: string): Promise<string> {
  const { data, error } = await sb().storage.from('cad-derived').createSignedUrl(path, 3600);
  if (error || !data) throw new Error(`부착 이미지 URL 생성 실패: ${error?.message ?? '알 수 없음'}`);
  return data.signedUrl;
}

/**
 * PDF 한 페이지를 래스터화해서 부착(Attach) 참조 이미지로 만든다.
 *
 * ★ 축척(scaleDenom) — reader.ts(PDFIMPORT)와 같은 문제: PDF 좌표(pt)는
 *   종이 크기이지 실제 매장 치수가 아니다. `mm = pt × (25.4/72) × scaleDenom`.
 */
export async function createUnderlayFromPdf(params: {
  projectId: string;
  drawingId: string;
  versionId?: string | null;
  file: File | Blob;
  scaleDenom: number;
  /** 배치 중심점(µm) — 보통 현재 화면 중심. 실제 좌상단은 크기를 안 뒤에 계산한다. */
  center: Pt;
  pageIndex?: number;
}): Promise<UnderlayRef> {
  // pdfjs-dist 는 DOM(Canvas/Image) 에 의존해 Node 테스트 러너에서는 모듈
  // 최상단에서 그냥 import 만 해도 죽는다 — 실제로 부착을 만들 때만 늦게 불러온다.
  const { rasterizePdfPage, blobToDataUrl } = await import('../cad/format/pdf/attach');
  const { blob, widthPt, heightPt } = await rasterizePdfPage(params.file, {
    pageIndex: params.pageIndex,
  });
  const mmPerPt = (25.4 / 72) * params.scaleDenom;
  const widthUm = Math.round(widthPt * mmPerPt * UM_PER_MM);
  const heightUm = Math.round(heightPt * mmPerPt * UM_PER_MM);

  const src = isDevPreview()
    ? await blobToDataUrl(blob)
    : await uploadUnderlayImage({
        projectId: params.projectId,
        drawingId: params.drawingId,
        versionId: params.versionId,
        blob,
      });

  return {
    id: newEntityId(),
    src,
    at: { x: params.center.x - widthUm / 2, y: params.center.y + heightUm / 2 },
    widthUm,
    heightUm,
    rotation: 0,
    opacity: 1,
    visible: true,
  };
}

/* -------------------------------------------------------------------------- *
 * 변환 작업 큐
 * -------------------------------------------------------------------------- */

export type JobStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_warnings'
  | 'failed';

export interface FileJob {
  id: string;
  drawing_id: string | null;
  source_id: string | null;
  kind: 'import' | 'preview' | 'thumbnail' | 'virus_scan';
  status: JobStatus;
  progress: number;
  attempts: number;
  max_attempts: number;
  error_code: string | null;
  error_message: string | null;
  // 워커가 만드는 경고와 같은 종류다(worker/src/engine.ts 의 ConvertWarning).
  // 코드 목록이 어긋나면 화면에서 라벨을 못 찾아 코드 원문이 그대로 보인다.
  warnings: ConversionWarning[];
  result: Record<string, unknown>;
  created_at: string;
  finished_at: string | null;
}

/** 변환 작업을 큐에 넣는다. 실제 변환은 워커가 한다. */
export async function enqueueImport(params: {
  drawingId: string;
  sourceId: string;
  priority?: number;
}): Promise<string> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 변환을 요청할 수 없습니다.');

  const { data, error } = await sb()
    .from('cad_file_job')
    .insert({
      drawing_id: params.drawingId,
      source_id: params.sourceId,
      kind: 'import',
      status: 'queued',
      priority: params.priority ?? 100,
    })
    .select('id')
    .single();

  if (error) throw new Error(`변환 요청 실패: ${error.message}`);
  return (data as { id: string }).id;
}

export async function getJob(jobId: string): Promise<FileJob | null> {
  const { data, error } = await sb()
    .from('cad_file_job')
    .select('*')
    .eq('id', jobId)
    .maybeSingle();
  if (error) throw new Error(`작업 조회 실패: ${error.message}`);
  return (data as FileJob | null) ?? null;
}

/**
 * 작업 진행 상황을 실시간으로 구독한다.
 * Realtime 이 끊겨도 사용자가 멈춘 화면을 보지 않도록 폴링을 함께 돌린다.
 */
export function watchJob(
  jobId: string,
  onChange: (job: FileJob) => void
): () => void {
  if (isDevPreview()) return () => {};

  const client = sb();

  const channel = client
    .channel(`cad_file_job:${jobId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'cad_file_job', filter: `id=eq.${jobId}` },
      (payload) => onChange(payload.new as FileJob)
    )
    .subscribe();

  // 안전망 — Realtime 이 막힌 망에서도 진행 상황이 보여야 한다
  const timer = window.setInterval(() => {
    void getJob(jobId)
      .then((j) => {
        if (!j) return;
        onChange(j);
        if (j.status !== 'queued' && j.status !== 'processing') {
          window.clearInterval(timer);
        }
      })
      .catch(() => {
        /* 폴링 실패는 조용히 넘긴다 — Realtime 이 살아 있을 수 있다 */
      });
  }, 3000);

  return () => {
    void client.removeChannel(channel);
    window.clearInterval(timer);
  };
}

/** 실패한 작업 재시도 */
export async function retryJob(jobId: string): Promise<void> {
  const { error } = await sb()
    .from('cad_file_job')
    .update({ status: 'queued', error_code: null, error_message: null, progress: 0 })
    .eq('id', jobId);
  if (error) throw new Error(`재시도 실패: ${error.message}`);
}

/* -------------------------------------------------------------------------- *
 * 다운로드 — 만료 URL + 감사 로그
 * -------------------------------------------------------------------------- */

/**
 * 만료되는 다운로드 링크를 만든다.
 *
 * 원본 저장소를 직접 공개하지 않는다 (기획서 11.3절).
 * 모든 반출은 감사 로그에 남긴다 — 누가 언제 무엇을 가져갔는지 추적 가능해야 한다.
 */
export async function createDownloadUrl(params: {
  bucket: 'cad-originals' | 'cad-derived' | 'cad-exports';
  path: string;
  expiresInSec?: number;
  audit: { project?: string; drawing?: string; version?: string; filename?: string };
}): Promise<string> {
  const { data, error } = await sb()
    .storage.from(params.bucket)
    .createSignedUrl(params.path, params.expiresInSec ?? 300);

  if (error || !data) throw new Error(`다운로드 링크 생성 실패: ${error?.message ?? '알 수 없음'}`);

  await logActivity('file.download', {
    project: params.audit.project,
    drawing: params.audit.drawing,
    version: params.audit.version,
    payload: { bucket: params.bucket, path: params.path, filename: params.audit.filename },
  });

  return data.signedUrl;
}
